import React, { useRef, useState } from 'react';
import { LinearProgress } from '@mui/material';
import { useSnackbar } from 'material-ui-snackbar-provider';

import Modal from '../../../modules/Modal';
import styles from './DeleteRowModal.module.scss';
import { ContainedButton, OutlinedButton } from '../../../modules/Buttons';
import { findSearchValue } from '../../../../helpers';
import {
  createComposerTopic,
  deleteTopicInComposer,
  deleteTopicInReflexComposer,
  getComposerDataByVideoFeedIdForVideoComments,
} from '../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { setFetchComments } from '../../../../store/composers/composersSlice';
import { triggerRefetch } from '../../../../store/sharedStore/sharedStoreSlice';
import { TComposerClip } from '..';
import { CLIP, TOPIC } from '../../../../enums';
import { useHotkeys } from 'react-hotkeys-hook';
import { UndoAction } from '../..';

type TDeleteRowModal = {
  switchModal: () => void;
  open: boolean;
  topic: TComposerClip;
  setUndoStack: (stack: any) => void;
  clipUndoMapRef: React.MutableRefObject<Record<string, UndoAction[]>>;
  renameUndoMapRef: React.MutableRefObject<Record<string, UndoAction[]>>;
  switchAdditionalDeleteComposerModal: () => void;
  beforeMutatingComposer?: () => Promise<boolean>;
};

const DeleteRowModal: React.FC<TDeleteRowModal> = ({
  switchModal,
  open,
  topic,
  setUndoStack,
  clipUndoMapRef,
  renameUndoMapRef,
  switchAdditionalDeleteComposerModal,
  beforeMutatingComposer,
}) => {
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { composerRows } = useAppSelector((state) => state.composersStore);

  const pushUndo = (action: UndoAction) => setUndoStack((s: any) => [...s, action]);
  const deleteRow = async () => {
    const feedId = findSearchValue('feedId') as string;
    const pageType = findSearchValue('pageType') as string;
    if (beforeMutatingComposer) {
      const canProceed = await beforeMutatingComposer();
      if (!canProceed) return;
    }
    setLoading(true);

    const oldTopicId = topic.id;
    const clipBucket = clipUndoMapRef.current[oldTopicId] || [];
    const renameBucket = renameUndoMapRef.current[oldTopicId] || [];

    try {
      const { data } = await getComposerDataByVideoFeedIdForVideoComments(feedId);
      const topicBackup = data.topics.find((t: TOPIC) => t.id === topic.id)!;

      pushUndo({
        exec: async () => {
          // a) recreate the topic → returns newTopic.id
          const { data: newTopic } = await createComposerTopic(topicBackup.name, feedId, topicBackup.topicIndex, topicBackup);
          const newTopicId = newTopic.id;

          // b) rebind only this topic’s clip undos to the new ID
          [...clipBucket, ...renameBucket].forEach((action) => {
            const original = action.exec;
            action.exec = async (overrideId?: number) => {
              // call original with newTopicId instead of oldTopicId
              await original(newTopicId);
            };
          });
          // move the bucket key in the map
          delete clipUndoMapRef.current[oldTopicId];
          clipUndoMapRef.current[newTopicId] = clipBucket;
          delete renameUndoMapRef.current[oldTopicId];
          renameUndoMapRef.current[newTopicId] = renameBucket;

          // c) refresh UI
          dispatch(triggerRefetch());
          dispatch(setFetchComments());
        },
      });

      // 3️⃣ delete topic
      switch (pageType) {
        case 'MY_COMPOSER':
          await deleteTopicInReflexComposer(topic.id, feedId);
          break;
        case 'COMPOSER':
          await deleteTopicInComposer(topic.id, feedId);
          break;
        default:
          break;
      }

      if (composerRows.length === 1) {
        switchAdditionalDeleteComposerModal();
      }

      dispatch(triggerRefetch());
      switchModal();
    } catch (err: any) {
      console.error(err);
      notification.showMessage(err?.response?.data?.message || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={!loading ? switchModal : console.log} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          Are you sure you want to delete <br /> "{topic.name}" {topic.clips.length ? 'with all the clips' : ''}
        </div>
        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={deleteRow} kind="default" disabled={loading}>
              <div ref={submitButtonRef}>Yes, I am sure</div>
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={switchModal} disabled={loading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteRowModal;
